import { ReactNode } from 'react'
import _ from 'lodash'
import styled from '@emotion/styled'
import { FusionColor } from '../../../theme/FusionStyle'
import { SecondaryCode } from '@fusion-advantage/backcompat'
import { ContraStatus, contraStatus } from '@fusion-advantage/formlib'
import { L2TabularListContent, L2TabularListHeader, L2TabularListRoot } from '../l2/L2TabularList'
import L2ControlGrid from '../l2/L2ControlGrid'
import L1Title from '../l1/L1Title'
import L2ControlGridLayout from '../l2/L2ControlGridLayout'
import { L1AcceptedCodeGel, L1CodeGel, L1ErrorCodeGel } from '../l1/L1CodeGel'

const TitleContainer = styled.div(({theme}) => ({
	// extra padding below title, for whatever reason
	marginBottom: theme.spacing(2),
}))

interface SecondaryCodingTableRowRootProps {
	backgroundColor: FusionColor
}

const SecondaryCodingTableRowRoot = styled.div<SecondaryCodingTableRowRootProps>(({theme, backgroundColor}) => ({
	gridColumn: 'rowstartln / rowendln',
	display: 'grid',
	gridTemplateColumns: 'subgrid',
	border: `1px solid ${theme.colors.lines}`,
	backgroundColor: theme.colors[backgroundColor],
	fontSize: '16px',
	alignItems: 'center',
	'> *': {
		padding: theme.spacing(2),
	},
}))

const VictimCodeGel = styled(L1CodeGel)(({theme}) => ({
	backgroundColor: theme.colors.third,
	color: theme.colors.inputLabel,
}))

const FalloutContent = styled.div({
	textAlign: 'right',
})

const FalloutIcd = styled.span({
	fontWeight: 'bold',
})

type SecondaryCodingTableRowProps = {
	model: SecondaryCode,
}

const SecondaryCodingTableRow = ({model}: SecondaryCodingTableRowProps) => {
	let GelComponent = L1AcceptedCodeGel
	let backgroundColor: FusionColor = FusionColor.White
	let fallout: ReactNode = null
	switch(contraStatus(model)) {
		case ContraStatus.Victim:
			GelComponent = VictimCodeGel
			backgroundColor = FusionColor.Second
			fallout = 'Victim'
			break
		case ContraStatus.Conflict:
			GelComponent = L1ErrorCodeGel
			backgroundColor = FusionColor.Second
			fallout = <>Conflict with <FalloutIcd>{model.Contra}</FalloutIcd></>
			break
	}
	return (
		<SecondaryCodingTableRowRoot backgroundColor={backgroundColor}>
			<div>{model.ICD10_Description}</div>
			<FalloutContent>{fallout}</FalloutContent>
			<div><GelComponent>{model.ICD10}</GelComponent></div>
		</SecondaryCodingTableRowRoot>
	)
}

interface L3SecondaryCodingTableProps {
	models: SecondaryCode[]
}

const L3SecondaryCodingTable = ({models}: L3SecondaryCodingTableProps) => {
	return (
		<L2ControlGrid layout={L2ControlGridLayout.FlexColumn}>
			<TitleContainer>
				<L1Title tier={3} title="Submitted ICD Codes" />
			</TitleContainer>
			<L2TabularListRoot columnCount={3} gridTemplateColumns="1fr max-content max-content">
				<L2TabularListHeader color={FusionColor.TableLabel}>
					<div>Name/Description</div>
					<div />
					<div>ICD</div>
				</L2TabularListHeader>
				<L2TabularListContent>
					{models.map((model, idx) => <SecondaryCodingTableRow model={model} key={idx} />)}
				</L2TabularListContent>
			</L2TabularListRoot>
		</L2ControlGrid>
	)
}

export default L3SecondaryCodingTable